import React, { useState, useEffect, useCallback } from "react";
import { db, auth } from "../../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  addDoc,
  setDoc,
  serverTimestamp,
  getDoc
} from "firebase/firestore";
import { useAuthState } from "react-firebase-hooks/auth";
import { Link } from "react-router-dom";
import styles from "./JoinTeamPage.module.css";

// SVG Icons as components
const BackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const XIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/>
    <line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const AlertIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/>
    <line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const RefreshIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6"/>
    <path d="M1 20v-6h6"/>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/>
    <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
  </svg>
);

// Alert component for notifications
const Alert = ({ type, message, onClose }) => {
  if (!message) return null;
  
  const Icon = type === "error" ? AlertIcon : CheckIcon;
  const alertClass = `${styles.alert} ${type === "error" ? styles.errorAlert : styles.successAlert}`;
  
  return (
    <div className={alertClass}>
      <span className={styles.alertIcon}><Icon /></span>
      <span>{message}</span>
      <button className={styles.closeButton} onClick={onClose}>
        <XIcon />
      </button>
    </div>
  );
};

// Invitation item component
const InviteItem = ({ invite, teamName, onAccept, onDecline, processing }) => (
  <div className={styles.inviteItem}>
    <div className={styles.inviteContent}>
      <div className={styles.inviteDetails}>
        <h3 className={styles.teamName}>{teamName || "Team Invitation"}</h3>
        <p className={styles.inviteInfo}>
          <span>Team ID:</span> {invite.teamId}
        </p>
        <p className={styles.inviteInfo}>
          <span>Sent to:</span> {invite.invitedEmail}
        </p>
        {invite.invitedBy && (
          <p className={styles.inviteInfo}>
            <span>Invited by:</span> {invite.invitedBy}
          </p>
        )}
      </div>
      <div className={styles.inviteActions}>
        <button
          onClick={() => onAccept(invite)}
          disabled={processing === invite.id}
          className={styles.acceptButton}
        >
          {processing === invite.id ? (
            <span className={styles.buttonSpinner}></span>
          ) : (
            <span className={styles.iconWrapper}><CheckIcon /></span>
          )}
          Accept
        </button>
        <button
          onClick={() => onDecline(invite.id)}
          disabled={processing === invite.id}
          className={styles.declineButton}
        >
          {processing === invite.id ? (
            <span className={`${styles.buttonSpinner} ${styles.dark}`}></span>
          ) : (
            <span className={styles.iconWrapper}><XIcon /></span>
          )}
          Decline
        </button>
      </div>
    </div>
  </div>
);

// Empty state component
const EmptyState = () => (
  <div className={styles.emptyState}>
    <p className={styles.emptyMessage}>No pending team invitations found.</p>
    <p className={styles.emptyDescription}>
      When coaches invite you to join their team, invitations will appear here.
    </p>
  </div>
);

// Loading indicator component
const LoadingIndicator = ({ message }) => (
  <div className={styles.loadingContainer}>
    <div className={styles.spinner}></div>
    <p className={styles.loadingText}>{message || "Loading..."}</p>
  </div>
);

function JoinTeamPage() {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user] = useAuthState(auth);
  const [teamNames, setTeamNames] = useState({});
  const [processing, setProcessing] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Fetch invites for the logged-in user's email
  const fetchInvites = useCallback(async (email) => {
    if (!email || !user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // First check if we have proper authentication
      if (!user.uid) {
        throw new Error("You must be logged in to view invitations");
      }
      
      console.log(`Fetching invites for ${email} with UID ${user.uid}`);
      
      // Modified query to fix invitations not showing
      const invitesRef = collection(db, "invitations");
      const invitesQuery = query(
        invitesRef,
        where("invitedEmail", "==", email)
      );
      
      const snapshot = await getDocs(invitesQuery);
      
      if (snapshot.empty) {
        console.log("No invites found");
        setInvites([]);
        setTeamNames({});
        setLoading(false);
        return;
      }
      
      // Filter in-memory to ensure we only show active invites
      const inviteList = snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter(invite => !invite.used && !invite.declined);
      
      console.log(`Found ${inviteList.length} active invites out of ${snapshot.docs.length} total`);
      setInvites(inviteList);
      
      // Fetch team names for each invitation
      const teamIds = [...new Set(inviteList.map(invite => invite.teamId))];
      console.log(`Fetching names for ${teamIds.length} teams`);
      
      const teamNamesObj = {};
      
      for (const teamId of teamIds) {
        try {
          // Try fetching the team document directly by ID
          const teamDocRef = doc(db, "teams", teamId);
          const teamDoc = await getDoc(teamDocRef);
          
          if (teamDoc.exists()) {
            teamNamesObj[teamId] = teamDoc.data().name || "Unknown Team";
          } else {
            console.log(`Team document not found for ID: ${teamId}`);
            teamNamesObj[teamId] = "Unknown Team";
          }
        } catch (err) {
          console.error(`Error fetching team name for ${teamId}:`, err);
          teamNamesObj[teamId] = "Unknown Team";
        }
      }
      
      setTeamNames(teamNamesObj);
    } catch (error) {
      console.error("Error fetching invites:", error);
      if (error.code === 'permission-denied') {
        setError("Permission denied: You don't have access to view these invitations.");
      } else {
        setError(`Failed to load invites: ${error.message}`);
      }
    }
    
    setLoading(false);
  }, [user]);

  // Load invites on component mount or user change
  useEffect(() => {
    if (user && user.email) {
      console.log(`User authenticated: ${user.uid}, email: ${user.email}`);
      // Add a small delay to ensure Firebase auth is fully initialized
      const timer = setTimeout(() => {
        fetchInvites(user.email.trim());
      }, 500);
      
      return () => clearTimeout(timer);
    } else {
      console.log("No authenticated user detected");
      setInvites([]);
      setTeamNames({});
    }
  }, [user, fetchInvites]);

  // Handle accepting an invite
  const handleAcceptInvite = async (invite) => {
    if (!user) {
      setError("You must be logged in to accept an invite.");
      return;
    }
    
    // Verify email match - security check
    if (invite.invitedEmail !== user.email) {
      setError("This invitation was not sent to your email address.");
      return;
    }
    
    const { teamId, id: inviteId, invitedEmail } = invite;
    setProcessing(inviteId);
    setError(null);
    
    try {
      // Verify invite hasn't been used or declined already
      const inviteRef = doc(db, "invitations", inviteId);
      const inviteSnapshot = await getDoc(inviteRef);
      
      if (!inviteSnapshot.exists()) {
        throw new Error("Invitation not found");
      }
      
      const inviteData = inviteSnapshot.data();
      if (inviteData.used) {
        throw new Error("This invitation has already been used");
      }
      if (inviteData.declined) {
        throw new Error("This invitation has already been declined");
      }
      
      // 1. Mark the invitation as used
      await updateDoc(inviteRef, { 
        used: true,
        acceptedAt: serverTimestamp(),
        acceptedBy: user.uid
      });
      
      console.log("Invitation marked as used");
      
      // 2. Add user to team members array
      const teamRef = doc(db, "teams", teamId);
      const teamDoc = await getDoc(teamRef);
      
      if (!teamDoc.exists()) {
        throw new Error("Team not found");
      }
      
      const teamData = teamDoc.data();
      const members = teamData.members || [];
      
      // Only add if user isn't already a member
      if (!members.includes(user.uid)) {
        await updateDoc(teamRef, {
          members: [...members, user.uid]
        });
        console.log(`Added user ${user.uid} to team members`);
      }
      
      // 3. Look up or create player document
      let playerId = null;

      // First try to find a player with this email
      try {
        const playersRef = collection(db, "players");
        const existingPlayerQuery = query(playersRef, where("email", "==", invitedEmail));
        const existingPlayerSnap = await getDocs(existingPlayerQuery);
        
        if (!existingPlayerSnap.empty) {
          // Use existing player document
          const existingPlayer = existingPlayerSnap.docs[0];
          playerId = existingPlayer.id;
          console.log("Found existing player with ID:", playerId);
        } else {
          // No existing player found, create a new player document with an auto-generated ID
          const newPlayerData = {
            email: invitedEmail,
            firstName: user.displayName ? user.displayName.split(' ')[0] : '',
            lastName: user.displayName ? user.displayName.split(' ').slice(1).join(' ') : '',
            gender: "",
            phone: "",
            relationship: "",
            isManager: false,
            parentUID: user.uid,
            createdAt: serverTimestamp(),
            summaryStats: {
              points: 0,
              rebounds: 0,
              assists: 0,
              steals: 0,
              blocks: 0,
              gamesPlayed: 0
            },
          };
          
          const newPlayerRef = await addDoc(collection(db, "players"), newPlayerData);
          playerId = newPlayerRef.id;
          console.log("Created new player with ID:", playerId);
        }
      } catch (playerError) {
        console.error("Error handling player document:", playerError);
        // Create a fallback player document if needed
        const newPlayerData = {
          email: invitedEmail,
          firstName: user.displayName ? user.displayName.split(' ')[0] : '',
          lastName: user.displayName ? user.displayName.split(' ').slice(1).join(' ') : '',
          parentUID: user.uid,
          createdAt: serverTimestamp(),
        };
        
        const newPlayerRef = await addDoc(collection(db, "players"), newPlayerData);
        playerId = newPlayerRef.id;
        console.log("Created fallback player with ID:", playerId);
      }
      
      // 4. Check for existing roster entries
      const rostersRef = collection(db, "rosters");
      const rosterQuery = query(
        rostersRef,
        where("teamId", "==", teamId),
        where("playerEmail", "==", invitedEmail)
      );
      
      console.log(`Checking for roster entries for teamId=${teamId}, playerEmail=${invitedEmail}`);
      const rosterSnap = await getDocs(rosterQuery);
  
      if (rosterSnap.empty) {
        console.log("Creating new roster entry");
        // Create a new roster entry with status "active"
        await addDoc(rostersRef, {
          playerId, // Use the found or created player ID
          userId: user.uid, // Keep track of the user ID separately
          teamId,
          playerName: user.displayName || '',
          playerEmail: invitedEmail,
          seasonId: "",
          startDate: serverTimestamp(),
          endDate: null,
          status: "active",
          joinedAt: serverTimestamp(),
          createdAt: serverTimestamp()
        });
      } else {
        // Update the existing roster entry
        const rosterDoc = rosterSnap.docs[0];
        console.log(`Updating existing roster entry: ${rosterDoc.id}`);
        await updateDoc(rosterDoc.ref, { 
          status: "active",
          playerId: playerId, // Make sure to update with correct player ID
          userId: user.uid, // Add/update user ID
          updatedAt: serverTimestamp()
        });
      }
  
      const teamName = teamNames[teamId] || "the team";
      setSuccess(`You've successfully joined ${teamName}!`);
      
      // Remove the accepted invite from the list
      setInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
    } catch (error) {
      console.error("Error accepting invite:", error);
      
      // Provide more user-friendly error messages
      if (error.code === 'not-found') {
        setError("This invitation may have been removed or already used.");
      } else if (error.code === 'permission-denied') {
        setError("You don't have permission to accept this invitation.");
      } else {
        setError(`Failed to accept invite: ${error.message}`);
      }
    }
    
    setProcessing(null);
  };

  // Handle declining an invite
  const handleDeclineInvite = async (inviteId) => {
    if (!user) {
      setError("You must be logged in to decline an invite.");
      return;
    }
    
    setProcessing(inviteId);
    setError(null);
    
    try {
      // Find the invite to ensure it belongs to this user
      const inviteToDecline = invites.find(inv => inv.id === inviteId);
      
      if (!inviteToDecline) {
        throw new Error("Invitation not found");
      }
      
      // Verify email match - security check
      if (inviteToDecline.invitedEmail !== user.email) {
        throw new Error("This invitation was not sent to your email address");
      }
      
      console.log(`Declining invitation ${inviteId}`);
      
      // Check if invitation exists
      const inviteRef = doc(db, "invitations", inviteId);
      const inviteDoc = await getDoc(inviteRef);
      
      if (!inviteDoc.exists()) {
        throw new Error("Invitation not found");
      }
      
      // Mark as declined rather than deleting
      await updateDoc(inviteRef, {
        declined: true,
        declinedAt: serverTimestamp(),
        declinedBy: user.uid
      });
      
      console.log("Invitation marked as declined");
      setSuccess("Invite declined successfully.");
      setInvites((prev) => prev.filter((inv) => inv.id !== inviteId));
    } catch (error) {
      console.error("Error declining invite:", error);
      
      // Provide more user-friendly error messages
      if (error.code === 'not-found') {
        setError("This invitation may have been removed or already processed.");
      } else if (error.code === 'permission-denied') {
        setError("You don't have permission to decline this invitation.");
      } else {
        setError(`Failed to decline invite: ${error.message}`);
      }
    }
    
    setProcessing(null);
  };

  // Handle declining all invites
  const handleDeclineAll = async () => {
    if (!user) {
      setError("You must be logged in to decline invites.");
      return;
    }
    
    if (invites.length === 0) return;
    
    if (!window.confirm("Are you sure you want to decline all invites?")) {
      return;
    }
    
    setProcessing("all");
    setError(null);
    
    try {
      console.log(`Declining ${invites.length} invitations`);
      
      // Process in batches to avoid overwhelming Firebase
      for (const invite of invites) {
        // Verify email match for each invite
        if (invite.invitedEmail !== user.email) {
          console.warn(`Skipping invite ${invite.id} - email mismatch`);
          continue;
        }
        
        const inviteRef = doc(db, "invitations", invite.id);
        const inviteDoc = await getDoc(inviteRef);
        
        if (!inviteDoc.exists()) {
          console.warn(`Invitation ${invite.id} not found, skipping`);
          continue;
        }
        
        await updateDoc(inviteRef, {
          declined: true,
          declinedAt: serverTimestamp(),
          declinedBy: user.uid
        });
        
        console.log(`Declined invitation ${invite.id}`);
      }
      
      console.log("All invitations declined successfully");
      setSuccess("All invites declined successfully.");
      setInvites([]);
    } catch (error) {
      console.error("Error declining all invites:", error);
      
      if (error.code === 'permission-denied') {
        setError("Permission denied: You don't have access to decline some invitations.");
      } else {
        setError(`Failed to decline all invites: ${error.message}`);
      }
    }
    
    setProcessing(null);
  };

  // Refresh invites
  const refreshInvites = () => {
    if (user && user.email) {
      fetchInvites(user.email.trim());
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>Team Invitations</h1>
        <Link to="/my-teams" className={styles.backLink}>
          <span className={styles.iconWrapper}><BackIcon /></span>
          Back to My Teams
        </Link>
      </div>
      
      <Alert 
        type="error" 
        message={error} 
        onClose={() => setError(null)} 
      />
      
      <Alert 
        type="success" 
        message={success} 
        onClose={() => setSuccess(null)} 
      />
      
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2 className={styles.cardTitle}>Your Active Invitations</h2>
          <button 
            onClick={refreshInvites}
            className={styles.refreshButton}
            disabled={loading}
          >
            <span className={styles.iconWrapper}><RefreshIcon /></span>
            Refresh
          </button>
        </div>
        
        {loading ? (
          <LoadingIndicator message="Loading your invitations..." />
        ) : invites.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className={styles.invitesList}>
              {invites.map((invite) => (
                <InviteItem
                  key={invite.id}
                  invite={invite}
                  teamName={teamNames[invite.teamId]}
                  onAccept={handleAcceptInvite}
                  onDecline={handleDeclineInvite}
                  processing={processing}
                />
              ))}
            </div>
            
            <div className={styles.footerActions}>
              <button
                onClick={handleDeclineAll}
                disabled={processing === "all" || loading}
                className={styles.declineAllButton}
              >
                {processing === "all" ? (
                  <span className={styles.buttonSpinner}></span>
                ) : (
                  <span className={styles.iconWrapper}><XIcon /></span>
                )}
                Decline All Invites
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default JoinTeamPage;